import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { useConfirm } from '@/components/ui/confirm';
import { useHasAnyRole } from '@/auth/hooks';
import { AppRole } from '@/auth/roles';
import { loginMethodLabel, userTypeLabel } from '@/lib/enums';
import { ROUTES } from '@/app/routes';
import { useChangeStatus, useUserDetail } from '../api';
import { UserHero } from '../components/UserHero';
import { InfoBox, InfoRow } from '../components/InfoBox';
import { EngagementSection } from '../components/EngagementSection';
import { UserModerationHistory } from '../components/UserModerationHistory';
import { UserAllContent } from '../components/UserAllContent';
import { SystemNotificationDialog } from '../components/SystemNotificationDialog';
import { ModerationHistoryPanel } from '../components/ModerationHistoryPanel';
import {
  formatDate,
  formatDevice,
  formatGender,
  formatKycStatus,
  USER_STATUS_BANNED,
  userStatusDisplay,
} from '../types';

export function UserDetailPage() {
  const { userId = '' } = useParams();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const changeStatus = useChangeStatus(userId);

  const { data: user, isLoading, isError, refetch } = useUserDetail(userId);

  const [notifyOpen, setNotifyOpen] = useState(false);

  const canModerate = useHasAnyRole([
    AppRole.Administrator,
    AppRole.SystemAdmin,
    AppRole.ContentManager,
  ]);

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  if (isError || !user) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3">
        <p className="text-sm text-muted-foreground">Failed to load user details.</p>
        <Button variant="outline" onClick={() => void refetch()}>
          Retry
        </Button>
      </div>
    );
  }

  const isBanned = user.userStatus?.toUpperCase() === USER_STATUS_BANNED;

  async function onBan() {
    const { confirmed, reason } = await confirm({
      title: 'Ban User',
      description:
        'This permanently bans the user (status TER). All their content will be hidden. This action is logged.',
      confirmText: 'Ban User',
      variant: 'destructive',
      requireReason: true,
    });
    if (!confirmed) return;
    changeStatus.mutate(
      {
        data: {
          userId: Number(userId),
          userStatus: USER_STATUS_BANNED,
          ...(reason?.trim() ? { reason: reason.trim() } : {}),
        },
      },
      {
        onSuccess: () => toast.success('User banned.'),
        onError: () => toast.error('Failed to ban user.'),
      },
    );
  }

  return (
    <div className="flex flex-col gap-4 p-6">
      {/* Breadcrumb (parity DetailsScreen.kt:164-180) */}
      <nav className="text-sm text-muted-foreground">
        <button type="button" className="hover:underline" onClick={() => void navigate(-1)}>
          User Management
        </button>
        <span className="px-2">›</span>
        <span className="text-foreground">User Detail</span>
      </nav>

      {/* Figma User Details: left ~2/5 profile panels, right ~3/5 engagement/earnings. */}
      <div className="grid gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
        {/* LEFT: hero, User Summary, Verification, Moderation History */}
        <div className="flex flex-col gap-4">
          <UserHero user={user} onBack={() => void navigate(-1)} />

          <InfoBox title="User Summary">
            <InfoRow label="Role" value={userTypeLabel(user.userType)} />
            <InfoRow
              label="Send System Notification"
              clickable
              onClick={() => setNotifyOpen(true)}
            />
            {/* No backend endpoints (doc 06 §8) — chevron stubs matching Figma. */}
            <InfoRow label="Log Out from All Devices" chevron />
            <InfoRow label="User Activity History" chevron />
            <InfoRow label="Payment History" chevron />
            <InfoRow label="Gender" value={formatGender(user.gender)} />
            <InfoRow label="Date of Birth" value={formatDate(user.birthday)} />
            <InfoRow
              label="Account Status"
              value={userStatusDisplay(user.userStatus, user.deactivatedAt).label}
            />
            {user.deactivatedAt ? (
              <InfoRow label="Deactivated At" value={formatDate(user.deactivatedAt)} />
            ) : null}
            <InfoRow label="Registration Date" value={formatDate(user.createdAt)} />
            <InfoRow label="Last Login Date" value={formatDate(user.lastLogin)} />
            <InfoRow label="Login Method" value={loginMethodLabel(user.loginMethod)} />
            <InfoRow label="Location" value={user.location} />
            <InfoRow label="Device" value={formatDevice(user.deviceName, user.osName)} />
          </InfoBox>

          <InfoBox title="Verification">
            <InfoRow label="Email" value={user.email} />
            <InfoRow label="Phone" value={user.phoneNumber ?? 'Unlinked'} />
            <InfoRow
              label="KYC Verification Status"
              value={formatKycStatus(user.accountVerification)}
            />
          </InfoBox>

          <ModerationHistoryPanel
            numberOfReports={user.numberOfReports}
            canModerate={canModerate}
            isBanned={isBanned}
            onBan={() => void onBan()}
            isBanning={changeStatus.isPending}
            onViewReports={() => void navigate(ROUTES.userModerationHistory(userId))}
          />
          <UserModerationHistory userId={userId} />
        </div>

        {/* RIGHT: engagement / content. All Content goes LAST: it grows without bound. */}
        <div className="flex flex-col gap-4">
          <EngagementSection userId={userId} />
          <UserAllContent userId={userId} canModerate={canModerate} />
        </div>
      </div>

      <SystemNotificationDialog
        open={notifyOpen}
        onOpenChange={setNotifyOpen}
        userId={userId}
      />
    </div>
  );
}
